/**
 * `AIRealtimeSessionModel.ts` — port of Unity's `AIRealtimeSessionModel.cs`
 * (session host): owns transport + `RealtimeSession` lifecycle, reconnect,
 * restart-on-personality/reset, mute/PTT state, and usage aggregation.
 * Protocol state stays in `RealtimeSession`; UI state stays behind
 * `AIInterfaceModel`.
 */

import { Emitter } from "@core/events/emitter";
import {
  AISessionUsageTracker,
  type UsageDelta,
} from "@ai/observability/AISessionUsageTracker";
import type { IAIObservabilityModel } from "@ai/observability/AIObservabilityModel";
import type { IRealtimeConversationLog } from "@ai/observability/RealtimeConversationLog";
import type { IAIAgentToolsModel } from "@ai/registry/AIAgentToolRegistryModel";
import type { AgentToolExecutorModel } from "@ai/executor/AgentToolExecutorModel";
import { isAiEnabled } from "@ai/aiConfig";
import { AIRealtimeTransportModel } from "./AIRealtimeTransportModel";
import { AIRealtimeTransportModelRTC } from "./AIRealtimeTransportModelRTC";
import type {
  IRealtimeTransport,
  RealtimeMicrophoneReason,
  RealtimeTransportKind,
} from "./IRealtimeTransport";
import {
  RealtimeSession,
  type IRealtimeSessionPolicyProvider,
} from "./RealtimeSession";

const RECONNECT_DELAY_MS = 4000;

export type RealtimeSessionStatus =
  | "disabled"
  | "disconnected"
  | "connecting"
  | "connected";

/**
 * Routes user turns AWAY from the realtime model when the active role thinks
 * on a non-realtime model (the Responses text bridge). Registered by the
 * orchestrator; `null` = every turn stays realtime.
 */
export interface ExternalTurnRouter {
  /** True = the turn was consumed (the bridge answers; the session only keeps the item for context). */
  tryRouteUserTurn(text: string, inputType: "text" | "voice"): boolean;
  /** Force-stop pressed / conversation reset: abort an in-flight bridged turn. */
  cancelActiveTurn(): void;
}

export class AIRealtimeSessionModel {
  readonly onStatusChanged = new Emitter<RealtimeSessionStatus>();
  /** See {@link isReadyForUserInput}. Emitted on the transition only. */
  readonly onReadyForUserInputChanged = new Emitter<boolean>();
  readonly onResponseDelta = new Emitter<string>();
  readonly onAssistantTextCompleted = new Emitter<string>();
  readonly onThinkingStateChanged = new Emitter<boolean>();
  readonly onUserVoiceStarted = new Emitter<void>();
  readonly onUserVoiceStopped = new Emitter<void>();
  readonly onUserTranscriptDelta = new Emitter<string>();
  readonly onUserTranscriptCompleted = new Emitter<string>();
  readonly onToolExecutionStarted = new Emitter<{ toolName: string }>();
  readonly onToolExecutionCompleted = new Emitter<{ toolName: string; result: string }>();
  readonly onUsageChanged = new Emitter<void>();
  readonly onErrorOccurred = new Emitter<string>();
  /** Unity `AgentSpeakingStateChanged` — drives activity release after audio. */
  readonly onAgentSpeakingChanged = new Emitter<boolean>();
  /**
   * The microphone opened or closed.
   *
   * Web-only — Unity has no counterpart, because nothing there reads the mute
   * state as a display value. Here `useSpark.isListening` does, and until this
   * existed it was only ever CORRECT BY ACCIDENT: every writer of `setMuted`
   * also flipped `isConstantListening` or `isPushToTalkActive`, which are the
   * two refs that computed does track, so it recomputed for the right answer
   * for the wrong reason. The first mute-only caller (`AiPanel`'s hold while
   * Spark speaks) made it stale — a panel reading "Listening…" over a closed
   * mic, which is the one failure that block calls worse than not shipping.
   */
  readonly onMutedChanged = new Emitter<boolean>();
  /**
   * Reasoning-summary surface, published only by the Responses text bridge —
   * the realtime models expose no reasoning at all. Kept on the session host
   * rather than given to the UI as a second event source, for the same reason
   * every other `notifyExternal*` signal is: the panel must not have to know
   * which transport answered.
   */
  readonly onReasoningStarted = new Emitter<void>();
  readonly onReasoningDelta = new Emitter<string>();
  /** Turn over; the payload is the wall-clock the panel reports as "Thought for …". */
  readonly onReasoningCompleted = new Emitter<number>();
  /** Response lifecycle transitions (`response.created` / `response.done`). */
  readonly onActiveResponseChanged = new Emitter<boolean>();

  readonly usageTracker = new AISessionUsageTracker();

  private readonly m_toolsModel: IAIAgentToolsModel;
  private readonly m_executor: AgentToolExecutorModel;
  private readonly m_policy: IRealtimeSessionPolicyProvider;
  private readonly m_observability: IAIObservabilityModel;
  private readonly m_log: IRealtimeConversationLog;

  private m_transport: IRealtimeTransport | null = null;
  private m_session: RealtimeSession | null = null;
  /** Model the LIVE socket connected on (null between sessions) — see `connectedModel`. */
  private m_connectedModel: string | null = null;
  /** Transport the LIVE session connected with — see `connectedTransport`. */
  private m_connectedTransport: RealtimeTransportKind | null = null;
  private m_turnRouter: ExternalTurnRouter | null = null;

  /**
   * Whether assistant text belongs to workflow-script narration rather than a
   * learner turn.
   *
   * Set by {@link sendUserText} when the caller identifies itself as `script`
   * and cleared only when a genuine user turn begins. Assistant completion is
   * deliberately NOT the boundary: one realtime response may publish more than
   * one text-completion event, and a consecutive `say` may be posted before a
   * late completion from the previous line is delivered. Consuming a boolean on
   * the first completion therefore labels the remaining / next scripted line as
   * an ordinary answer, which makes it appear in the workflow chat and opens the
   * drawer. The next user turn is the semantic boundary shared by every number
   * of authored `say` lines.
   *
   * ⚠ It cannot be derived from the `INTERNAL SAY:` prefix, which is the
   * obvious-looking test: the Responses text bridge delivers its own replies
   * through that same contract, and those ARE answers. The caller has to say.
   *
   * A scripted line that posts and never produces output may leave this set,
   * but the next typed, spoken, image, or externally-routed user turn clears it
   * before its answer is published. The consumer uses the label only to hide
   * narration the workflow panel already shows.
   */
  private m_isWorkflowScriptSpeech = false;
  private m_status: RealtimeSessionStatus = "disconnected";
  private m_isMuted = true;
  private m_microphoneReason: RealtimeMicrophoneReason = "session_sync";
  private m_shouldRun = false;
  private m_reconnectTimer: number | null = null;
  private readonly m_sessionOffFns: Array<() => void> = [];

  constructor(
    toolsModel: IAIAgentToolsModel,
    executor: AgentToolExecutorModel,
    policy: IRealtimeSessionPolicyProvider,
    observability: IAIObservabilityModel,
    log: IRealtimeConversationLog,
  ) {
    this.m_toolsModel = toolsModel;
    this.m_executor = executor;
    this.m_policy = policy;
    this.m_observability = observability;
    this.m_log = log;
  }

  /**
   * The live `session.update` payload for the debug panel's "Context" tab —
   * see `RealtimeSession.buildSessionPayload`.
   *
   * Answers even with NO session up, by standing one up is not required: the
   * payload is built from the policy and the tools model, both of which exist
   * from construction. That matters because Spark being offline is exactly when
   * a developer wants to read what it WOULD send.
   */
  buildSessionPayload(): Record<string, unknown> {
    if (this.m_session) return this.m_session.buildSessionPayload();
    return RealtimeSession.buildSessionPayloadFrom(this.m_policy, this.m_toolsModel);
  }

  /** The per-turn `User Context:` message (Unity `BuildTurnContext`), for the debug panel. */
  buildTurnUserContext(): string | null {
    return this.m_policy.buildTurnUserContext();
  }

  get status(): RealtimeSessionStatus {
    return this.m_status;
  }

  /**
   * Unity `AddResponsesApiUsage` — helper subagents (Responses API) land their
   * token usage in the same per-source tracker the realtime session uses.
   */
  addResponsesApiUsage(source: string, delta: UsageDelta): void {
    this.usageTracker.add(source, delta);
    this.onUsageChanged.emit();
  }

  get hasSession(): boolean {
    return this.m_session !== null && this.m_status === "connected";
  }

  /**
   * Whether an outgoing user item would actually reach the model.
   *
   * ⚠ NOT {@link hasSession}, and the gap is a real one: `status` goes
   * `"connected"` when the TRANSPORT connects, while the server session only
   * exists once `session.created` arrives ~450 ms later. See
   * `RealtimeSession.isReadyForUserInput`.
   */
  get isReadyForUserInput(): boolean {
    return this.m_session?.isReadyForUserInput ?? false;
  }

  /**
   * Resolves TRUE once {@link isReadyForUserInput}, or FALSE on timeout / abort.
   *
   * For a caller whose message has nobody behind it to notice the silence and
   * retry — a workflow script's authored `say` line is the case this exists
   * for. An ordinary user turn deliberately does NOT wait: it would queue text
   * that replays stale after a reconnect, and the user can simply retype.
   *
   * ⚠ Does NOT gate on `m_shouldRun`, which is the whole point. The
   * orchestrator awaits three prompt-catalog fetches BEFORE calling
   * `startAsync`, so during app boot the session has not been asked to run
   * yet — and that boot window is exactly what a reload-time say lands in.
   * `isAiEnabled()` is the honest "is there ever going to be a session" test.
   *
   * Subscribes to THIS model's emitter rather than the session's: a reconnect
   * builds a new `RealtimeSession`, and a waiter bound to the old one would
   * never hear the new one become ready.
   */
  waitUntilReadyForUserInputAsync(timeoutMs: number, signal?: AbortSignal): Promise<boolean> {
    if (this.isReadyForUserInput) return Promise.resolve(true);
    if (!isAiEnabled()) return Promise.resolve(false);
    if (signal?.aborted) return Promise.resolve(false);

    return new Promise<boolean>((resolve) => {
      let settled = false;
      const finish = (ready: boolean): void => {
        if (settled) return;
        settled = true;
        off();
        signal?.removeEventListener("abort", onAbort);
        window.clearTimeout(timer);
        resolve(ready);
      };
      function onAbort(): void {
        finish(false);
      }
      const off = this.onReadyForUserInputChanged.on(() => {
        if (this.isReadyForUserInput) finish(true);
      });
      const timer = window.setTimeout(() => finish(false), timeoutMs);
      signal?.addEventListener("abort", onAbort, { once: true });
      // Re-check after subscribing: readiness can land between the getter above
      // and the listener being attached.
      if (this.isReadyForUserInput) finish(true);
    });
  }

  /**
   * The model the current session's websocket connected with, or null when no
   * session is up. The orchestrator compares this against the active role's
   * resolved model to decide whether a role/settings change needs a reconnect
   * (the model rides the websocket URL, so it cannot change in-session).
   */
  get connectedModel(): string | null {
    return this.m_connectedModel;
  }

  /**
   * Which transport the current session actually connected with, or null when
   * no session is up. Compared against the resolved policy to decide whether a
   * settings change needs a reconnect — the transport decides which class is
   * constructed, so like the model it cannot change in-session.
   *
   * ⚠ Not the same as the RESOLVED transport: after a sticky fallback the
   * policy still resolves `websocket` (the failed one is in the orchestrator's
   * rejected set), so the two agree and no reconnect loop forms.
   */
  get connectedTransport(): RealtimeTransportKind | null {
    return this.m_connectedTransport;
  }

  get sessionId(): string {
    return this.m_session?.sessionId ?? "";
  }

  get isMuted(): boolean {
    return this.m_isMuted;
  }

  get isAgentSpeaking(): boolean {
    return this.m_transport?.isAgentSpeaking ?? false;
  }

  get micInputLevel(): number {
    return this.m_transport?.micInputLevel ?? 0;
  }

  get hasActiveResponse(): boolean {
    return this.m_session?.hasActiveResponse ?? false;
  }

  get isThinking(): boolean {
    return this.m_session?.isThinking ?? false;
  }

  // -- Lifecycle -------------------------------------------------------

  async startAsync(): Promise<void> {
    if (!isAiEnabled()) {
      this.setStatus("disabled");
      return;
    }
    this.m_shouldRun = true;
    await this.connectAsync("start");
  }

  stop(): void {
    this.m_shouldRun = false;
    this.clearReconnectTimer();
    this.teardownSession();
    this.setStatus(isAiEnabled() ? "disconnected" : "disabled");
  }

  /** Unity `RestartRealtimeSession` (reset conversation / personality). */
  async restartAsync(reason: string): Promise<boolean> {
    if (!this.m_shouldRun) return false;
    this.teardownSession();
    return await this.connectAsync(reason);
  }

  dispose(): void {
    this.stop();
  }

  private async connectAsync(reason: string): Promise<boolean> {
    if (!this.m_shouldRun || this.m_session) return this.hasSession;
    this.setStatus("connecting");

    // Resolved ONCE per connect: the model rides the websocket URL, so this is
    // the session's model for the socket's whole life (role-driven changes
    // reconnect through the orchestrator's session-model policy).
    const modelConfig = this.m_policy.resolveSessionConnection();
    const transport: IRealtimeTransport =
      modelConfig.transport === "webrtc"
        ? new AIRealtimeTransportModelRTC(modelConfig.model, this.m_log)
        : new AIRealtimeTransportModel(modelConfig.model, this.m_log);
    const session = new RealtimeSession(
      transport,
      this.m_toolsModel,
      this.m_executor,
      this.m_policy,
      this.m_log,
    );
    this.m_log.note(
      "session.connect",
      `reason=${reason}, id=${session.sessionId}, model=${modelConfig.model}, ` +
        `transport=${modelConfig.transport} (attempted)`,
    );
    this.m_transport = transport;
    this.m_session = session;
    this.m_connectedModel = modelConfig.model;
    this.m_connectedTransport = modelConfig.transport;
    this.wireSession(session, transport);

    const connected = await transport.connectAsync();
    // ⚠ A transport may emit a failure code SYNCHRONOUSLY from inside its own
    // `connectAsync` — every `AIRealtimeTransportModelRTC` failure path does —
    // and `Emitter.emit` is a plain synchronous loop. That reaches the
    // orchestrator's sticky transport fallback, which answers by calling
    // `restartAsync`: it tears THIS session down and installs a healthy
    // WebSocket replacement, all before this line resumes.
    //
    // Without this guard the resumed frame then acts on a session it no longer
    // owns: the failure branch below would `teardownSession()` the very
    // replacement the fallback just created (leaving Spark with no session and
    // a "connected" status), and the success branch would promote a session
    // that has already been disposed.
    if (this.m_session !== session) return false;
    if (!connected || !this.m_shouldRun) {
      this.teardownSession();
      if (this.m_shouldRun) {
        this.setStatus("disconnected");
        this.scheduleReconnect();
      }
      return false;
    }

    this.setStatus("connected");
    // The `session.connect` note above records the transport ATTEMPTED — it is
    // written before the await, so a failed WebRTC attempt logs first and the
    // successful WebSocket one logs second. This is the note that answers
    // "which transport is live".
    this.m_log.note(
      "session.connected",
      `id=${session.sessionId}, model=${modelConfig.model}, transport=${modelConfig.transport}`,
    );
    transport.setMuted(this.m_isMuted, this.m_microphoneReason);
    this.m_observability.trackSessionStarted(session.sessionId, reason, modelConfig.model);
    return true;
  }

  private wireSession(session: RealtimeSession, transport: IRealtimeTransport): void {
    this.m_sessionOffFns.push(
      session.onResponseDelta.on((d) => this.onResponseDelta.emit(d)),
      session.onAssistantTextCompleted.on((t) => {
        // Do not clear workflow-script origin here. A completion is an output
        // part boundary, not a user-turn boundary; see
        // `m_isWorkflowScriptSpeech`.
        this.onAssistantTextCompleted.emit(t);
      }),
      session.onThinkingStateChanged.on((t) => this.onThinkingStateChanged.emit(t)),
      session.onUserVoiceStarted.on(() => {
        // Server VAD can begin answering before transcription completes. Clear
        // at the start of the learner's voice turn so its first answer delta is
        // never mistaken for the preceding workflow narration.
        this.m_isWorkflowScriptSpeech = false;
        this.onUserVoiceStarted.emit();
      }),
      session.onUserVoiceStopped.on(() => this.onUserVoiceStopped.emit()),
      session.onUserTranscriptDelta.on((d) => this.onUserTranscriptDelta.emit(d)),
      session.onUserTranscriptCompleted.on((t) => {
        // Defense in depth for transports that complete a transcript without a
        // preceding voice-start event.
        if (t.trim().length > 0) this.m_isWorkflowScriptSpeech = false;
        this.onUserTranscriptCompleted.emit(t);
      }),
      session.onToolExecutionStarted.on((e) => this.onToolExecutionStarted.emit(e)),
      session.onToolExecutionCompleted.on((e) => this.onToolExecutionCompleted.emit(e)),
      session.onActiveResponseChanged.on((active) => this.onActiveResponseChanged.emit(active)),
      session.onReadyForUserInputChanged.on((ready) =>
        this.onReadyForUserInputChanged.emit(ready),
      ),
      transport.onAgentSpeakingChanged.on((speaking) => this.onAgentSpeakingChanged.emit(speaking)),
      session.onError.on((code) => {
        this.m_observability.trackError(session.sessionId, code);
        this.onErrorOccurred.emit(code);
      }),
      session.onUsage.on((usage) => {
        this.usageTracker.add(usage.source, usage);
        this.trackRealtimeUsage(usage, session.sessionId);
        this.onUsageChanged.emit();
      }),
      transport.onDisconnected.on(() => {
        if (!this.m_shouldRun) return;
        this.teardownSession();
        this.setStatus("disconnected");
        this.scheduleReconnect();
      }),
    );
  }

  private teardownSession(): void {
    // A waiter parked across this must re-evaluate: `m_session` is about to
    // become null, and the session's own disconnect emit does not fire for a
    // teardown we initiate.
    const wasReady = this.isReadyForUserInput;
    if (this.m_session) {
      this.m_observability.trackSessionSummary(
        this.m_session.sessionId,
        this.usageTracker.totalTokens,
        this.usageTracker.totalEstimatedUsd,
      );
    }
    for (const off of this.m_sessionOffFns) off();
    this.m_sessionOffFns.length = 0;
    this.m_session?.shutdown();
    this.m_session = null;
    this.m_transport?.dispose();
    this.m_transport = null;
    this.m_connectedModel = null;
    this.m_connectedTransport = null;
    this.m_isWorkflowScriptSpeech = false;
    if (wasReady) this.onReadyForUserInputChanged.emit(false);
  }

  private trackRealtimeUsage(usage: UsageDelta & { source?: string }, sessionId: string): void {
    if (usage.source !== "Realtime") return;

    const realtimeBucket = this.usageTracker.buckets.find((bucket) => bucket.source === "Realtime");
    this.m_observability.trackRealtimeUsage(sessionId, usage as UsageDelta & { readonly source: string }, {
      totalTokens: this.usageTracker.totalTokens,
      totalEstimatedUsd: this.usageTracker.totalEstimatedUsd,
      realtimeTokens: realtimeBucket
        ? realtimeBucket.inputTokens
          + realtimeBucket.outputTokens
          + realtimeBucket.inputAudioTokens
          + realtimeBucket.outputAudioTokens
        : 0,
      realtimeEstimatedUsd: realtimeBucket?.estimatedUsd ?? 0,
    });
  }

  private scheduleReconnect(): void {
    this.clearReconnectTimer();
    this.m_reconnectTimer = window.setTimeout(() => {
      this.m_reconnectTimer = null;
      if (this.m_shouldRun && !this.m_session) void this.connectAsync("reconnect");
    }, RECONNECT_DELAY_MS);
  }

  private clearReconnectTimer(): void {
    if (this.m_reconnectTimer !== null) {
      window.clearTimeout(this.m_reconnectTimer);
      this.m_reconnectTimer = null;
    }
  }

  private setStatus(status: RealtimeSessionStatus): void {
    if (this.m_status === status) return;
    this.m_status = status;
    this.onStatusChanged.emit(status);
  }

  // -- Session commands -------------------------------------------------

  sendUserText(
    text: string,
    inputType: "text" | "voice" = "text",
    origin: "user" | "script" = "user",
  ): void {
    if (!this.m_session) return;
    // Marked AFTER the session check, so a say the transport never accepted
    // cannot label somebody else's later answer. See
    // `m_isWorkflowScriptSpeech`.
    this.m_isWorkflowScriptSpeech = origin === "script";
    // Unity `RefreshSessionContextForLatestUserTurn`: rebuild instructions +
    // project context on each user turn so the agent sees current state.
    this.m_session.refreshSessionContext();
    this.m_observability.trackUserMessage(this.m_session.sessionId, text, inputType);
    // `INTERNAL SAY:` lines are SPEECH COMMANDS for the realtime model (script
    // playback, and the bridge's own reply delivery) — never routable, or the
    // bridge would swallow its own output.
    const routed =
      !text.startsWith("INTERNAL SAY:") &&
      (this.m_turnRouter?.tryRouteUserTurn(text, inputType) ?? false);
    // A routed turn still posts the item so the realtime conversation keeps
    // the user's words for context — it just must not answer them.
    this.m_session.sendUserText(text, true, !routed);
  }

  /**
   * The Responses text bridge's presentation + routing seam. The bridge runs
   * entirely outside the realtime protocol, but its activity must reach the
   * SAME panel surfaces (thinking ring, tool cards, error notices) — so the
   * orchestrator re-publishes it through this host rather than giving the UI
   * a second event source to subscribe to.
   */
  /** @see m_isWorkflowScriptSpeech — read synchronously while creating an entry. */
  get isWorkflowScriptSpeech(): boolean {
    return this.m_isWorkflowScriptSpeech;
  }

  setExternalTurnRouter(router: ExternalTurnRouter | null): void {
    this.m_turnRouter = router;
  }

  notifyExternalThinking(thinking: boolean): void {
    this.onThinkingStateChanged.emit(thinking);
  }

  /**
   * Publishes one completed response from an external turn router directly to
   * the normal assistant-text surface. Used when a Responses-family role is
   * configured for text-only replies: no realtime `response.create`, so no
   * audio and no second model turn.
   */
  notifyExternalAssistantText(text: string): void {
    if (!text) return;
    // This is an answer produced for a routed learner turn, never workflow
    // narration. Clear before the first delta creates its history entry.
    this.m_isWorkflowScriptSpeech = false;
    // One full-text delta preserves first-token latency tracking and the
    // panel's pending-entry path; completion then settles that same entry.
    this.onResponseDelta.emit(text);
    this.onAssistantTextCompleted.emit(text);
  }

  notifyExternalToolExecutionStarted(toolName: string): void {
    this.onToolExecutionStarted.emit({ toolName });
  }

  notifyExternalToolExecutionCompleted(toolName: string, result: string): void {
    this.onToolExecutionCompleted.emit({ toolName, result });
  }

  notifyExternalError(code: string): void {
    this.onErrorOccurred.emit(code);
  }

  notifyExternalReasoningStarted(): void {
    this.onReasoningStarted.emit();
  }

  notifyExternalReasoningDelta(delta: string): void {
    if (delta) this.onReasoningDelta.emit(delta);
  }

  notifyExternalReasoningCompleted(durationMs: number): void {
    this.onReasoningCompleted.emit(durationMs);
  }

  /** `caption` rides the same conversation item — see `RealtimeSession`. */
  sendUserImage(dataUrl: string, caption?: string): boolean {
    const sent = this.m_session?.sendUserImage(dataUrl, caption) ?? false;
    if (sent) this.m_isWorkflowScriptSpeech = false;
    return sent;
  }

  /**
   * Posts a user item into the realtime conversation WITHOUT asking for an
   * answer and WITHOUT offering it to the turn router.
   *
   * The bridge's own text turns already do this via `sendUserText` — the
   * router consumes them and the item is posted `requestResponse: false` so
   * the voice model keeps the user's words for context. A file attachment has
   * no text to route, so it needs this direct path: without it the realtime
   * side of a bridged conversation silently loses the fact that a document was
   * ever handed over.
   */
  postUserContextText(text: string): void {
    if (!text?.trim()) return;
    this.m_session?.sendUserText(text, false, false);
  }

  setMuted(
    muted: boolean,
    reason: RealtimeMicrophoneReason = "manual",
  ): void {
    const changed = this.m_isMuted !== muted;
    this.m_isMuted = muted;
    this.m_microphoneReason = reason;
    // The transport call is UNCONDITIONAL, the emit is not: a caller
    // re-asserting a mode the model already believes it is in still needs the
    // microphone brought into line with it (see
    // `AIInterfaceModel.setConstantListeningEnabled`), while a listener only
    // wants the transitions.
    this.m_transport?.setMuted(muted, reason);
    if (changed) this.onMutedChanged.emit(muted);
  }

  forceStop(): boolean {
    // A bridged turn is "the active response" while the bridge owns the role's
    // turns — the stop button must reach it too.
    this.m_turnRouter?.cancelActiveTurn();
    return this.m_session?.forceStopActiveResponse() ?? false;
  }

  refreshSessionContext(): void {
    this.m_session?.refreshSessionContext();
  }

  /**
   * A line for the model to read, never answered and never shown — see
   * `RealtimeSession.sendContextNote`. Deliberately NOT {@link sendUserText}:
   * that path refreshes the session, counts a user message, may route the text
   * to the bridge as a turn, and asks for a reply.
   */
  sendContextNote(text: string): boolean {
    return this.m_session?.sendContextNote(text) ?? false;
  }

  resumeAudioOutputIfNeeded(): void {
    this.m_transport?.resumeAudioOutputIfNeeded();
  }
}
